<template>
  <!-- Detailed View -->
  <div v-if="view === 'detailed'" :class="['rounded-lg px-4 py-3 md:p-6', gradientClass]">
    <div class="flex items-center justify-between mb-4">
      <div :class="['w-11 h-11 rounded-full flex items-center justify-center', iconBgClass]">
        <span class="material-symbols-outlined text-2xl">{{ mainIcon }}</span>
      </div>
      <span v-if="showTrend" class="material-symbols-outlined text-2xl opacity-70">{{ trendIcon }}</span>
    </div>
    <div class="flex md:flex-col items-center md:items-start gap-4 mb-2">
        <h3 class="text-3xl font-bold mb-1">{{ formattedCount }}</h3>
        <p :class="['text-sm', subtitleClass]">{{ subtitle }}</p>
    </div>
  </div>

  <!-- Simple View (Filter Button) -->
  <button
    v-else
    :class="[
      'relative flex items-center space-x-3 px-2 py-1 md:px-6 md:py-3 rounded-xl border-2 text-sm md:text-base transition-all overflow-hidden',
      //simpleViewClasses.bg,
      simpleViewClasses.text,
      simpleViewClasses.border,
      simpleViewClasses.hover,
      //{ 'ring-2 ring-offset-2': isActive }
      { [simpleViewClasses.bg]: isActive }
    ]"
    @click="handleClick"
  >
    <!-- Active indicator - filled top-right corner -->
      <!--
    <span
      v-if="isActive"
      :class="['absolute top-0 right-0 w-5 h-5', simpleViewClasses.bg]"
      style="clip-path: polygon(100% 0, 100% 100%, 0 0)"
    ></span>
    -->

    <span :class="['material-symbols-outlined text-xl', simpleViewClasses.iconColor]">{{ mainIcon }}</span>
    <span class="md:hidden font-semibold">{{ subtitleMobile || subtitle }}</span>
    <span class="hidden md:block font-semibold">{{ subtitle }}</span>
    <span :class="['px-2 py-0.5 rounded-full text-xs font-bold', badgeClasses]">
      {{ formattedCount }}
    </span>
  </button>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  count: {
    type: Number,
    required: true
  },
  subtitle: {
    type: String,
    required: true
  },
  subtitleMobile: {
    type: String
  },
  variant: {
    type: String,
    required: true,
    validator: (value) => ['video', 'group', 'tutor', 'material'].includes(value)
  },
  view: {
    type: String,
    default: 'detailed',
    validator: (value) => ['detailed', 'simple'].includes(value)
  },
  showTrend: {
    type: Boolean,
    default: true
  },
  isActive: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['click']);

const variantConfig = {
  video: {
    gradient: 'bg-brand text-brand-contrast',
    subtitle: 'text-brand-contrast/80',
    mainIcon: 'play_circle',
    trendIcon: 'trending_up',
    simple: {
      bg: 'bg-brand-soft',
      text: 'text-brand-soft-text',
      border: 'border-line-brand',
      hover: 'hover:border-brand hover:bg-brand-soft',
      iconColor: 'text-brand',
      badge: { bg: 'bg-brand-soft', text: 'text-brand-soft-text' }
    }
  },
  group: {
    gradient: 'bg-surface border border-line',
    subtitle: 'text-muted',
    mainIcon: 'groups',
    trendIcon: 'trending_up',
    simple: {
      bg: 'bg-brand-soft',
      text: 'text-brand-soft-text',
      border: 'border-line-brand',
      hover: 'hover:border-brand hover:bg-brand-soft',
      iconColor: 'text-brand',
      badge: { bg: 'bg-brand-soft', text: 'text-brand-soft-text' }
    }
  },
  tutor: {
    gradient: 'bg-surface border border-line',
    subtitle: 'text-muted',
    mainIcon: 'person',
    trendIcon: 'trending_up',
    simple: {
      bg: 'bg-brand-soft',
      text: 'text-brand-soft-text',
      border: 'border-line-brand',
      hover: 'hover:border-brand hover:bg-brand-soft',
      iconColor: 'text-brand',
      badge: { bg: 'bg-brand-soft', text: 'text-brand-soft-text' }
    }
  },
  material: {
    gradient: 'bg-surface border border-line',
    subtitle: 'text-muted',
    mainIcon: 'description',
    trendIcon: 'trending_up',
    simple: {
      bg: 'bg-brand-soft',
      text: 'text-brand-soft-text',
      border: 'border-line-brand',
      hover: 'hover:border-brand hover:bg-brand-soft',
      iconColor: 'text-brand',
      badge: { bg: 'bg-brand-soft', text: 'text-brand-soft-text' }
    }
  }
};

const gradientClass = computed(() => variantConfig[props.variant].gradient);
const subtitleClass = computed(() => variantConfig[props.variant].subtitle);
const mainIcon = computed(() => variantConfig[props.variant].mainIcon);
const trendIcon = computed(() => variantConfig[props.variant].trendIcon);

const iconBgClass = computed(() => (
  props.variant === 'video' ? 'bg-white/15 text-brand-contrast' : 'bg-brand-soft text-brand'
));

const activeCornerClass = computed(() => 'bg-brand');

const simpleViewClasses = computed(() => variantConfig[props.variant].simple);
const badgeClasses = computed(() => {
  const badge = variantConfig[props.variant].simple.badge;
  return `${badge.bg} ${badge.text}`;
});

const formattedCount = computed(() => {
  return props.count.toLocaleString();
});

const handleClick = () => {
  emit('click');
};
</script>
